import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import SaveConflict from '../../../common/components/save-conflict/save-conflict';
import { Notice, Tag } from '../../../common/components/detail/detail';
import { Gate, useAuth } from '../../../global/contexts/auth/auth';
import { useAuthorNames, useRepositoryName } from '../../../global/contexts/lookups/lookups';
import {
    usePlan, usePlanVersion, useApproveVersion, useCreateVersion,
    useRequestChanges, useSaveDraft, useSubmitVersion,
} from '../contexts/plans';
import {
    PLAN_TEMPLATE, SCOPE_ALL, SCOPE_OPTIONS, SCOPE_SOME,
    VERSION_STATE_HINT, VERSION_STATE_LABEL,
} from '../utils/form';
import {
    SCOPE_UNKNOWN, draftHasChanges, editorValuesOf, isDirty, nextBase,
    scopeText, toSaveDraftPayload,
} from '../utils/version';
import { renderMarkdown } from '../../../common/components/markdown/markdown';
import ApproveConfirm from '../forms/approve-confirm';
import MarkdownEditor from '../forms/markdown-editor';
import VersionBar, { BarAction } from './version-bar';
import Authorship from '../../audit/layouts/authorship';
import { IconFilePlus, IconReturn, IconSave, IconSeal, IconSend } from '../../../common/icons';
import CompareVersions from './compare';

import '../styles/plans.css';

const STATE_TONE = { approved: 'active', in_review: 'accent', draft: 'warn' };

export default function PlanScreen({ planId, versionNumber }) {
    const { data, isLoading, error } = usePlan(planId);

    if (isLoading) return <p className="plans__loading">Cargando plan...</p>;

    if (error) {
        return (
            <p className="plans__failure">
                {error.status === 404
                    ? 'Este plan no está disponible: no existe, o queda fuera de tu alcance.'
                    : error.message || 'No se pudo cargar el plan'}
            </p>
        );
    }

    const plan = data?.plan ?? data;
    const versions = data?.versions ?? plan?.versions ?? [];

    // Which version to open when nobody asked for one: the approved one, which
    // is the one the work came out of. Falling back to "the newest" would open
    // a draft somebody is still writing and present it as the plan.
    const approved = versions.find(v => v.state === 'approved');
    const current = versionNumber
        ?? approved?.number
        ?? versions[versions.length - 1]?.number
        ?? null;

    return (
        <section className="plans plans--screen">
            <header className="detail-head">
                <div className="detail-head__text">
                    <h2 className="detail-head__title">{plan.name}</h2>
                    <div className="detail-head__tags">
                        <Tag>Plan #{plan.id}</Tag>
                        {approved
                            ? <Tag tone="active">Aprobada: v{approved.number}</Tag>
                            : <Tag tone="warn">Sin versión aprobada</Tag>}
                    </div>
                </div>
                <div className="detail-head__actions">
                    {/* Dos vueltas, porque a un plan se llega por dos caminos:
                        la sección de Planeación y la pestaña de su proyecto. */}
                    <Link
                        to={plan.project_id ? `/admin/plans?project=${plan.project_id}` : '/admin/plans'}
                        className="detail-head__action detail-head__action--quiet"
                    >
                        Planeación
                    </Link>
                    {plan.project_id && (
                        <Link
                            to={`/admin/projects/${plan.project_id}/planeacion`}
                            className="detail-head__action detail-head__action--quiet"
                        >
                            Ver el proyecto
                        </Link>
                    )}
                </div>
            </header>

            {versions.length > 1 && (
                <nav className="plans__versions" aria-label="Versiones del plan">
                    {versions.map(version => (
                        <Link
                            key={version.number}
                            to={`/admin/plans/${plan.id}/v/${version.number}`}
                            className={`plans__version ${String(version.number) === String(current) ? 'plans__version--active' : ''}`}
                        >
                            v{version.number}
                            <span className="plans__version-state">
                                {VERSION_STATE_LABEL[version.state] ?? version.state}
                            </span>
                        </Link>
                    ))}
                </nav>
            )}

            {versions.length > 1 && (
                <Comparison planId={plan.id} versions={versions} current={current} />
            )}

            {current == null
                ? <Notice>Este plan todavía no tiene ninguna versión.</Notice>
                : <VersionPanel key={`${plan.id}:${current}`} planId={plan.id} planName={plan.name} number={current} />}
        </section>
    );
}

// Closed by default: comparing is something somebody asks for, and opening it
// unasked would fire a second version request on every plan anybody looks at.
function Comparison({ planId, versions, current }) {
    const [open, setOpen] = useState(false);

    return (
        <div>
            <button
                type="button"
                className="plans__tab"
                onClick={() => setOpen(value => !value)}
                aria-expanded={open}
            >
                {open ? 'Ocultar la comparación' : 'Comparar con otra versión'}
            </button>
            {open && <CompareVersions planId={planId} versions={versions} current={current} />}
        </div>
    );
}

function VersionPanel({ planId, planName, number }) {
    const { data, isLoading, error, refetch } = usePlanVersion(planId, number);

    if (isLoading) return <p className="plans__loading">Cargando versión...</p>;

    if (error) {
        return (
            <p className="plans__failure">
                {error.status === 404
                    ? 'Esta versión no está disponible.'
                    : error.message || 'No se pudo cargar la versión'}
            </p>
        );
    }

    // Keyed by the ROW and not by its revision. Remounting on every new revision
    // threw away whatever was typed the moment a conflict refetched the version
    // — the exact loss the 409 exists to prevent. Newer reads are adopted inside
    // `Version`, and only when nothing on screen would be lost.
    return (
        <Version
            key={data.id ?? number}
            planId={planId}
            planName={planName}
            number={number}
            version={data}
            refetch={refetch}
        />
    );
}

// `version` is always the adapted model from utils/version.js — never the raw
// body. It is the latest read; `base` is the one the editor is typed on.
function Version({ planId, planName, number, version, refetch }) {
    const { can } = useAuth();
    const { userName } = useAuthorNames();
    const repoName = useRepositoryName();

    const saveDraft = useSaveDraft(planId, number);
    const submit = useSubmitVersion(planId, number);
    const requestChanges = useRequestChanges(planId, number);
    const approve = useApproveVersion(planId, number);
    const createVersion = useCreateVersion(planId);

    // `base` is the version as it was read; the draft is typed on top of it and
    // the payload is diffed against it. Same discipline as the tier and the
    // task, and here it matters most: the text of a plan is the longest thing
    // anybody types into this panel.
    const [base, setBase] = useState(version);
    const [values, setValues] = useState(() => editorValuesOf(version));
    const [conflict, setConflict] = useState(false);
    const [confirming, setConfirming] = useState(null);
    // What the approval dialog shows, frozen when it opened.
    const [signing, setSigning] = useState(null);

    const dirty = isDirty(values, base);

    // A newer read (a refetch, a transition, somebody else's save) replaces the
    // base ONLY with nothing unsaved and no conflict open. Adjusted during
    // render, which is how React wants state derived from a prop change.
    const adopted = nextBase(base, version, { dirty, conflict });
    if (adopted !== base) {
        setBase(adopted);
        setValues(editorValuesOf(adopted));
    }

    const state = base.state;
    const editable = state === 'draft' && can('plans:write');
    const scopeUnknown = base.scope.status === SCOPE_UNKNOWN;
    const setContent = content => setValues(current => ({ ...current, content }));
    const setScope = scope => setValues(current => ({ ...current, scope }));

    const takeTheirs = async () => {
        const result = await refetch();
        if (result.error || !result.data) {
            toast.error(result.error?.message || 'No se pudo traer lo guardado');
            return;
        }
        setBase(result.data);
        setValues(editorValuesOf(result.data));
        setConflict(false);
    };

    const handleSave = async () => {
        const { payload, error } = toSaveDraftPayload(values, base);

        if (error) {
            toast.error(error);
            return;
        }
        if (!draftHasChanges(payload)) {
            toast('No hay cambios que guardar');
            return;
        }

        try {
            const saved = await saveDraft.mutateAsync(payload);
            setBase(saved);
            setConflict(false);
            // Saying it out loud, because it is the thing people get wrong:
            // ten saves leave ten snapshots and a single v1.
            toast.success(`Borrador guardado. Sigue siendo la versión ${saved.number}.`);
        } catch (err) {
            if (err.isRevisionConflict) {
                // The base keeps its old revision on purpose: what is refetched
                // is shown as «lo guardado», and nobody's text is replaced
                // until somebody chooses.
                setConflict(true);
                await refetch();
                return;
            }
            toast.error(err.message || 'No se pudo guardar el borrador');
        }
    };

    const run = async (mutation, payload, done) => {
        try {
            await mutation.mutateAsync(payload);
            toast.success(done);
            setConfirming(null);
        } catch (err) {
            // Each refusal names the gate that closed, and the panel shows THAT
            // and not "Forbidden". `sesion_humana` means a token was used where
            // a person is required; `lead` means the permission was there and
            // the role in the project was not. Sending somebody to ask for a
            // permission they already have is its own kind of wrong.
            setConfirming(null);
            toast.error(err.message || 'No se pudo completar la acción');
        }
    };

    const busy = saveDraft.isPending || submit.isPending
        || requestChanges.isPending || approve.isPending || createVersion.isPending;

    // Signing an unreadable scope, or a version with no text, is signing
    // something nobody on this screen saw.
    const signable = !scopeUnknown && !!base.content?.trim();

    return (
        <>
            {/* PINNED: which version, in which state, and what can be done
                with it now — in sight however far down the text goes. */}
            <VersionBar
                title={base.plan?.name ?? planName}
                number={base.number}
                stateLabel={VERSION_STATE_LABEL[state] ?? state}
                tone={STATE_TONE[state] ?? 'neutral'}
                revision={base.revision}
                dirty={editable && dirty}
            >
                {editable && (
                    <>
                        <BarAction
                            icon={<IconSave />}
                            label={saveDraft.isPending ? 'Guardando…' : 'Guardar borrador'}
                            onClick={handleSave}
                            disabled={busy || !dirty}
                            primary={dirty}
                            title={dirty ? 'Guardar borrador' : 'No hay cambios que guardar'}
                        />
                        <BarAction
                            icon={<IconSend />}
                            label="Enviar a revisión"
                            onClick={() => run(submit, base.revision, 'Versión presentada a revisión')}
                            disabled={busy || dirty}
                            primary={!dirty}
                            title={dirty ? 'Guardá primero: se presenta lo guardado, no lo que está en pantalla' : 'Enviar a revisión'}
                        />
                    </>
                )}

                {state === 'in_review' && (
                    <>
                        <Gate domain="plans:write">
                            <BarAction
                                icon={<IconReturn />}
                                label="Solicitar cambios"
                                onClick={() => setConfirming('changes')}
                                disabled={busy}
                            />
                        </Gate>
                        <Gate domain="plans:approve">
                            <BarAction
                                icon={<IconSeal />}
                                label="Aprobar"
                                primary
                                onClick={() => { setSigning(base); setConfirming('approve'); }}
                                disabled={busy || !signable}
                                title={signable ? 'Aprobar' : 'No se aprueba una versión sin contenido o con el alcance ilegible'}
                            />
                        </Gate>
                    </>
                )}

                {state === 'approved' && (
                    <Gate domain="plans:write">
                        {/* `source_version_id` es el ID DE LA FILA, no su número:
                            la ruta copia el contenido y el alcance de esa versión
                            aprobada en una instantánea nueva. */}
                        <BarAction
                            icon={<IconFilePlus />}
                            label="Preparar la siguiente versión"
                            onClick={() => run(createVersion, base.id, 'Se abrió la siguiente versión')}
                            disabled={busy || base.id == null}
                        />
                    </Gate>
                )}
            </VersionBar>

            {/* The dialogs open RIGHT UNDER THE BAR, where the click happened.
                At the bottom of the page they would open out of sight.

                THE CONFIRMATION IS FROZEN ON WHAT WAS READ. It is handed the
                version captured when the dialog opened, and signs with THAT
                revision: if the version changed underneath, the answer is a 409
                and not a signature over a text nobody read. */}
            {confirming === 'approve' && signing && (
                <ApproveConfirm
                    version={signing}
                    number={signing.number}
                    busy={approve.isPending}
                    onCancel={() => setConfirming(null)}
                    onConfirm={() => run(approve, signing.revision, `Versión ${signing.number} aprobada`)}
                />
            )}

            {confirming === 'changes' && (
                <RequestChanges
                    busy={requestChanges.isPending}
                    onCancel={() => setConfirming(null)}
                    onConfirm={(reason) => run(requestChanges, { revision: base.revision, reason }, 'Se devolvió con tu razón')}
                />
            )}

            <div className="plans__meta">
                <Tag tone={scopeUnknown ? 'warn' : undefined}>{scopeText(base.scope, repoName)}</Tag>
                {base.approval && (
                    <Tag tone="active">
                        Aprobada
                        {base.approval.approved_at
                            ? ` el ${new Date(base.approval.approved_at).toLocaleDateString()}`
                            : ''}
                        {base.approval.approved_by != null
                            ? ` por ${userName(base.approval.approved_by)}`
                            : ''}
                    </Tag>
                )}
                <span className="plans__hint">{VERSION_STATE_HINT[state]}</span>
            </div>

            {/* A plan records its authorship in columns, not in events:
                who created the plan, who saved this version last, who signed
                it. No `events:read` needed. */}
            <Authorship
                rows={[
                    { label: 'Plan creado por', actor: base.plan?.created_by != null ? { userId: base.plan.created_by } : null },
                    {
                        label: `Versión ${base.number} escrita por`,
                        actor: base.author_id != null ? { userId: base.author_id, at: base.updated_at } : null,
                    },
                    {
                        label: 'Aprobada por',
                        actor: base.approval?.approved_by != null
                            ? { userId: base.approval.approved_by, at: base.approval.approved_at }
                            : null,
                        empty: state === 'approved' ? 'Sin registro' : 'Todavía no aprobada',
                    },
                ]}
            />

            {scopeUnknown && (
                <Notice tone="warn">
                    No se pudo leer el <strong>alcance</strong> de esta versión. No se muestra como
                    «todo el proyecto», no se vuelve a guardar desde acá y la versión no se puede
                    aprobar hasta que se lea bien.
                </Notice>
            )}

            {conflict && (
                <SaveConflict
                    fields={[{
                        name: 'content',
                        label: 'Contenido',
                        mine: values.content,
                        theirs: version.content ?? '',
                    }]}
                    onReload={takeTheirs}
                    onDismiss={() => setConflict(false)}
                />
            )}

            {state === 'approved' && (
                <Notice tone="accent">
                    Estás leyendo la <strong>versión {base.number} aprobada</strong>. No se edita ni se
                    revoca: los cambios nuevos se preparan en otra versión, y los tiers que salieron
                    de esta <strong>siguen leyendo esta</strong> aunque se apruebe una posterior.
                </Notice>
            )}

            {state === 'in_review' && (
                <Notice tone="accent">
                    Esta versión está <strong>presentada</strong> y quedó congelada. Desde acá se
                    devuelve con una razón, o se aprueba.
                </Notice>
            )}

            {editable ? (
                <div className="plans__editor">
                    <MarkdownEditor
                        value={values.content}
                        onChange={setContent}
                        disabled={busy}
                    >
                        {!values.content.trim() && (
                            <button
                                type="button"
                                className="plans__template"
                                onClick={() => setContent(PLAN_TEMPLATE)}
                            >
                                Usar la plantilla
                            </button>
                        )}
                    </MarkdownEditor>

                    {!scopeUnknown && (
                        <ScopeChooser
                            scope={values.scope}
                            onScope={setScope}
                            names={values.repository_ids.map(repoName)}
                            disabled={busy}
                        />
                    )}

                    {dirty && (
                        <p className="plans__note">
                            Enviar a revisión presenta <strong>lo guardado</strong>, no lo que hay en
                            pantalla. Guardá primero.
                        </p>
                    )}
                </div>
            ) : (
                <article className="md plans__reader">
                    {renderMarkdown(base.content) ?? (
                        <p className="plans__note">Esta versión no tiene contenido.</p>
                    )}
                </article>
            )}

        </>
    );
}

function ScopeChooser({ scope, onScope, names, disabled }) {
    const count = names.length;
    return (
        <fieldset className="plans__scope" disabled={disabled}>
            <legend className="plans__scope-legend">Alcance de esta versión</legend>
            {SCOPE_OPTIONS.map(option => (
                <label key={option.value} className="plans__scope-option">
                    <input
                        type="radio"
                        name="plan-scope"
                        value={option.value}
                        checked={scope === option.value}
                        onChange={() => onScope(option.value)}
                    />
                    <span className="plans__scope-text">
                        <span className="plans__scope-label">{option.label}</span>
                        <span className="plans__scope-hint">{option.hint}</span>
                    </span>
                </label>
            ))}
            {scope === SCOPE_SOME && (
                <p className="plans__note">
                    {count > 0
                        ? `Elegido${count === 1 ? '' : 's'}: ${names.join(' · ')}.`
                        : 'Todavía no elegiste ninguno. Guardar así sería un alcance vacío, que el backend lee como el proyecto entero — por eso no se guarda hasta que elijas.'}
                </p>
            )}
            {scope === SCOPE_ALL && (
                <p className="plans__note">
                    Se guarda como una lista vacía, que en este contrato significa
                    {' '}<strong>el proyecto entero</strong> — no «sin repositorios».
                </p>
            )}
        </fieldset>
    );
}

function RequestChanges({ busy, onCancel, onConfirm }) {
    const [reason, setReason] = useState('');

    return (
        <div className="plans__dialog" role="dialog" aria-modal="true" aria-label="Solicitar cambios">
            <h3 className="plans__dialog-title">Solicitar cambios</h3>
            <p className="plans__note">
                La versión vuelve a borrador con tu razón. Sin la razón, quien la retome no sabe
                qué corregir.
            </p>
            <textarea
                className="plans__textarea plans__textarea--short"
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={4}
                aria-label="Razón"
                autoFocus
            />
            <div className="plans__actions">
                <button type="button" className="plans__button" onClick={onCancel} disabled={busy}>
                    Cancelar
                </button>
                <button
                    type="button"
                    className="plans__button plans__button--primary"
                    onClick={() => onConfirm(reason.trim())}
                    disabled={busy || !reason.trim()}
                >
                    Devolver a borrador
                </button>
            </div>
        </div>
    );
}
